import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	completeSetupRequest,
	getClientAccessRequest,
	getServicesRequest,
	getSummaryRequest,
	resetSetup,
	startSetupRequest,
} from 'redux/reducers/paymentMethods/reducer';
import {
	getPaymentMethodsBlockedMessage,
	getPaymentMethodSetup,
	getPaymentMethodSetupCompleting,
	getPaymentMethodSetupError,
	getPaymentMethodSetupLoading,
	getPaymentMethodSetupResult,
	getPaymentMethodsSummary,
	getPaymentServices,
} from 'redux/reducers/paymentMethods/selectors';
import {
	CONSENT_TEXT,
	METHOD_TYPE_LABELS,
	SETUP_COMPLETED_MESSAGE,
	SETUP_DUPLICATE_MESSAGE,
	SETUP_PROCESSING_MESSAGE,
} from 'redux/reducers/paymentMethods/constants';
import { TServicesSelection, TSetupMethodType } from 'services/api/paymentMethods/types';
import {
	createElement,
	ensureAirwallex,
	TDropInElement,
	TDropInOptions,
	canUseApplePay,
	loadApplePaySdk,
	threeDsPopupSize,
} from 'services/airwallex';
import { notificationContainer } from 'utils/notificationContainer';
import { DEFAULT_ERROR_MESSAGE } from 'utils/apiError';
import { WarningAlert } from 'ui/WarningAlert';
import { PaymentMethodIcon } from '../BrandIcons';
import { isServicesSelectionValid, ServicesSelector } from '../ServicesSelector';
import { ClientAccessBanner } from '../ClientAccess';

const DROP_IN_CONTAINER_ID = 'awx-dropin';

type TStep = 'select' | 'dropin' | 'result';

type TAddPaymentMethodDialogProps = {
	open: boolean;
	onClose: () => void;
};

const initialSelection = (): TServicesSelection => ({ allServices: true, serviceIds: [] });

export const AddPaymentMethodDialog: FC<TAddPaymentMethodDialogProps> = ({ open, onClose }) => {
	const dispatch = useDispatch();
	const summary = useSelector(getPaymentMethodsSummary);
	const services = useSelector(getPaymentServices);
	const setup = useSelector(getPaymentMethodSetup);
	const setupLoading = useSelector(getPaymentMethodSetupLoading);
	const setupError = useSelector(getPaymentMethodSetupError);
	const setupCompleting = useSelector(getPaymentMethodSetupCompleting);
	const setupResult = useSelector(getPaymentMethodSetupResult);
	// a new binding is blocked by the operator (a binding already started may still be completed)
	const blockedMessage = useSelector(getPaymentMethodsBlockedMessage);

	const [step, setStep] = useState<TStep>('select');
	const [methodType, setMethodType] = useState<TSetupMethodType | ''>('');
	const [selection, setSelection] = useState<TServicesSelection>(initialSelection());
	const [consentAccepted, setConsentAccepted] = useState(false);
	const [dropInReady, setDropInReady] = useState(false);
	const [dropInFailed, setDropInFailed] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	// Apple Pay: built in (Safari) or enabled by the Apple Pay JS SDK in other browsers (scan a code with an iPhone)
	const [applePay, setApplePay] = useState<boolean>(() => canUseApplePay());
	useEffect(() => {
		if (!open || applePay) return undefined;
		let cancelled = false;
		loadApplePaySdk().then(() => {
			if (!cancelled) setApplePay(canUseApplePay());
		});
		return () => {
			cancelled = true;
		};
	}, [open, applePay]);

	const availableTypes = useMemo<TSetupMethodType[]>(
		() => (summary?.setupMethods ?? []).filter((type) => type !== 'applepay' || applePay),
		[summary, applePay],
	);

	// every opening starts a new binding session: Drop-in options and the client secret are not reused
	useEffect(() => {
		if (!open) return;
		dispatch(resetSetup());
		setStep('select');
		setSelection(initialSelection());
		setConsentAccepted(false);
		setDropInReady(false);
		setDropInFailed(false);
		dispatch(getClientAccessRequest());
		if (!services) dispatch(getServicesRequest());
		if (!summary) dispatch(getSummaryRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (!methodType || !availableTypes.includes(methodType)) {
			setMethodType(availableTypes[0] ?? '');
		}
	}, [availableTypes, methodType]);

	// the binding session could not be started: back to step 1 (the error is shown as a toast)
	useEffect(() => {
		if (step === 'dropin' && !setupLoading && !setup && setupError) setStep('select');
	}, [step, setupLoading, setup, setupError]);

	// Drop-in: the options come from the gateway as a whole and are passed as is
	useEffect(() => {
		if (!open || step !== 'dropin' || !setup) return undefined;
		let disposed = false;
		let completed = false;
		let element: TDropInElement | null = null;
		let readyFallback: ReturnType<typeof setTimeout> | undefined;
		const { setupId } = setup;

		(async () => {
			try {
				await ensureAirwallex(setup.env);
				// a copy: the store state is frozen, the SDK may extend its options
				const options = JSON.parse(JSON.stringify(setup.dropIn)) as TDropInOptions;
				// 3-D Secure as a popup of the needed size instead of a full-screen page
				options.style = { ...options.style, ...threeDsPopupSize() };
				const created = await createElement('dropIn', options);
				if (disposed) {
					created?.destroy();
					return;
				}
				element = created;
				// The SDK attaches the event listeners to the DOM element passed to mount(): listeners registered
				// before mount() are lost (no "ready" → endless spinner, no "success" after 3-D Secure → nothing happens).
				created.mount(containerRef.current ?? DROP_IN_CONTAINER_ID);
				created.on('ready', () => {
					if (!disposed) setDropInReady(true);
				});
				created.on('success', (event) => {
					if (disposed || completed) return;
					completed = true;
					// the server checks the result with Airwallex itself; the ids are only hints
					dispatch(
						completeSetupRequest({
							setupId,
							intentId: event.detail?.intent?.id ?? null,
							consentId: event.detail?.consent?.id ?? null,
						}),
					);
					setStep('result');
				});
				created.on('error', (event) => {
					notificationContainer(event.detail?.error?.message ?? DEFAULT_ERROR_MESSAGE, 'error');
				});
				// the form is usually visible long before "ready" could be missed: never keep the spinner forever
				readyFallback = setTimeout(() => {
					if (!disposed) setDropInReady(true);
				}, 15000);
			} catch {
				if (!disposed) {
					setDropInFailed(true);
					notificationContainer(
						'The payment form could not be loaded. Please try again later.',
						'error',
					);
				}
			}
		})();

		return () => {
			disposed = true;
			if (readyFallback) clearTimeout(readyFallback);
			element?.destroy();
			element = null;
		};
	}, [open, step, setup, dispatch]);

	const handleClose = () => {
		dispatch(resetSetup());
		onClose();
	};

	const handleContinue = () => {
		if (!methodType || blockedMessage) return;
		setDropInReady(false);
		setDropInFailed(false);
		dispatch(
			startSetupRequest({
				methodType,
				allServices: selection.allServices,
				serviceIds: selection.allServices ? [] : selection.serviceIds,
				consentAccepted: true,
			}),
		);
		setStep('dropin');
	};

	const handleTryAgain = () => {
		dispatch(resetSetup());
		setDropInReady(false);
		setDropInFailed(false);
		setStep('select');
	};

	const canContinue =
		!!methodType &&
		consentAccepted &&
		isServicesSelectionValid(selection) &&
		!setupLoading &&
		!blockedMessage;

	const renderSelectStep = () => (
		<>
			{!summary && <CircularProgress size={24} />}
			{summary && availableTypes.length === 0 && (
				<WarningAlert>Adding a payment method is not available at the moment.</WarningAlert>
			)}
			{availableTypes.length > 0 && (
				<>
					<Typography color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
						Payment method
					</Typography>
					<RadioGroup
						row
						value={methodType}
						onChange={(event) => setMethodType(event.target.value as TSetupMethodType)}
					>
						{availableTypes.map((type) => (
							<FormControlLabel
								key={type}
								value={type}
								control={<Radio />}
								label={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
										<PaymentMethodIcon type={type} />
										{METHOD_TYPE_LABELS[type]}
									</Box>
								}
							/>
						))}
					</RadioGroup>
					<Typography color="primary" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
						Use for services
					</Typography>
					<ServicesSelector services={services} value={selection} onChange={setSelection} />
					<FormControlLabel
						sx={{ mt: 2, alignItems: 'flex-start' }}
						control={
							<Checkbox
								checked={consentAccepted}
								onChange={(event) => setConsentAccepted(event.target.checked)}
								sx={{ pt: 0 }}
							/>
						}
						label={<Typography sx={{ fontSize: '.875rem' }}>{CONSENT_TEXT}</Typography>}
					/>
				</>
			)}
		</>
	);

	const renderDropInStep = () => (
		<Box sx={{ minHeight: '200px' }}>
			{setup?.holdAmount ? (
				<Typography sx={{ fontSize: '.875rem', mb: 2 }}>
					To check {METHOD_TYPE_LABELS[setup.methodType]}, a temporary hold of{' '}
					{setup.holdAmount.toFixed(2)} {setup.holdCurrency ?? 'EUR'} is placed on it. The hold is
					released right after the check: no money is charged.
				</Typography>
			) : null}
			{(setupLoading || (!dropInReady && !dropInFailed)) && (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={28} />
				</Box>
			)}
			{dropInFailed && (
				<WarningAlert>The payment form could not be loaded. Please try again later.</WarningAlert>
			)}
			{setup && <div id={DROP_IN_CONTAINER_ID} ref={containerRef} />}
		</Box>
	);

	const renderResultStep = () => {
		if (setupCompleting || (!setupResult && !setupError)) {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
					<CircularProgress size={24} />
					<Typography>Checking the payment method…</Typography>
				</Box>
			);
		}
		if (setupError && !setupResult) {
			return <WarningAlert>{setupError}</WarningAlert>;
		}
		switch (setupResult?.status) {
			case 'completed':
				return <Typography>{SETUP_COMPLETED_MESSAGE}</Typography>;
			case 'duplicate':
				// the server text depends on the type ("This card is already saved" / "This payment method is already saved")
				return <WarningAlert>{setupResult.message || SETUP_DUPLICATE_MESSAGE}</WarningAlert>;
			case 'processing':
				return <Typography>{SETUP_PROCESSING_MESSAGE}</Typography>;
			default:
				return (
					<WarningAlert>{setupResult?.message || 'The payment method was not added.'}</WarningAlert>
				);
		}
	};

	const resultFailed =
		step === 'result' &&
		!setupCompleting &&
		(setupResult?.status === 'failed' ||
			setupResult?.status === 'expired' ||
			(!!setupError && !setupResult));

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">Add payment method</DialogTitle>
			<DialogContent>
				{step === 'select' &&
					(blockedMessage ? (
						// read-only: the operator's message instead of the form
						<ClientAccessBanner messages={[blockedMessage]} />
					) : (
						renderSelectStep()
					))}
				{step === 'dropin' && renderDropInStep()}
				{step === 'result' && renderResultStep()}
			</DialogContent>
			<DialogActions>
				{step === 'select' && (
					<>
						<Button onClick={handleClose}>Cancel</Button>
						<Button variant="contained" onClick={handleContinue} disabled={!canContinue}>
							Continue
						</Button>
					</>
				)}
				{step === 'dropin' && (
					<>
						<Button onClick={handleTryAgain}>Back</Button>
						<Button onClick={handleClose}>Cancel</Button>
					</>
				)}
				{step === 'result' && (
					<>
						{resultFailed && <Button onClick={handleTryAgain}>Try again</Button>}
						<Button variant="contained" onClick={handleClose}>
							Close
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};
